import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { librarySearchPath } from '@/constants/appRoutes';
import { Input } from '@/components/ui/input';
import { useContentHeaderLayout } from '@/contexts/BreadcrumbContext';

export function ShellSearch() {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState('');
  const [focused, setFocused] = useState(false);
  const compact = useContentHeaderLayout().compact;
  const showCompactPlaceholder = compact && !focused && !value;

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && value.trim()) {
      navigate(librarySearchPath(value.trim()));
      setValue('');
      inputRef.current?.blur();
    }
  };

  return (
    <div className="relative w-full min-w-0">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground xl:left-3.5" />
      <Input
        ref={inputRef}
        type="search"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder={
          showCompactPlaceholder
            ? 'Search…'
            : 'Search questions and answers…'
        }
        aria-label="Search"
        className="h-9 rounded-full border-border bg-[var(--surface-main)] pl-9 pr-9 text-sm shadow-none focus-visible:ring-1"
      />
      {!showCompactPlaceholder && (
        <kbd className="pointer-events-none absolute right-3 top-1/2 flex -translate-y-1/2 items-center gap-0.5 rounded border border-border bg-muted/60 px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
          ⌘ K
        </kbd>
      )}
    </div>
  );
}
